import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { CatalogService } from '../../../core/services/catalog.service';
import { LoggerService } from '../../../core/services/logger.service';
import { DataService } from '../../../core/services/data.service';
import { UtilsService } from '../../../core/services/utils.service';
import { AppStateInterface } from '../../../appState.interface';
import { selectedCustomerSelector } from '../../../core/store/selectors';
import { SelectedCustomerCacheInterface } from '../../../core/store/interface/selectedCustomerCache.interface';

import {
  faImage,
  faPalette,
  faFont,
  faInfoCircle,
  faUpload,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

interface GraphicsConfiguration {
  logo?: string; // Base64 encoded image or URL
  primaryColor?: string; // Hex color code
  portalTitle?: string; // Portal title text
}

@Component({
  selector: 'app-graphic-config',
  templateUrl: './graphic-config.component.html',
  styleUrls: ['./graphic-config.component.scss']
})
export class GraphicConfigComponent implements OnInit, OnDestroy, OnChanges {
  @Input() selectedAudience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER';
  /**
   * Customer su cui operare, passato dal contenitore (es. general-setting20, dove un admin può
   * selezionare un customer diverso dal proprio). Ha la precedenza sulla selezione nello store:
   * senza questo, il tab Grafica leggerebbe e scriverebbe la config del customer sbagliato mentre
   * gli altri tab seguono la selezione. Se null si ricade sullo store (settings-panel, che non ha
   * una selezione esplicita) — stessa convenzione di general-setting.
   */
  @Input() customerId: string | null = null;

  private destroy$ = new Subject<void>();
  private dataLoaded = false;

  // FontAwesome icons
  faImage = faImage;
  faPalette = faPalette;
  faFont = faFont;
  faInfoCircle = faInfoCircle;
  faUpload = faUpload;
  faTrash = faTrash;

  // Configuration data
  logo: string | null = null;
  primaryColor: string = '#1976d2'; // Default Material Blue, will be overridden by current theme
  portalTitle: string = '';

  // UI states
  isLoading = false;
  currentCustomerUid: string | null = null;
  public configId: string | null = null;
  public configCustomers: string[] | null = null;
  public configOwnerUid: string | null = null;

  // Logo preview
  logoPreview: string | null = null;

  // Store the default theme color for reset functionality
  defaultThemeColor: string = '#7bcd3b';

  // Customer theme color presets (matching app.component.ts setCustomerTheme)
  readonly themeColorPresets: string[] = [
    '#004379',  // Argo - rgb(0, 67, 121)
    '#00a1df',  // Landini - rgb(0, 161, 223)
    '#e63027',  // McCormick - rgb(230, 48, 39)
    '#4bab26',  // CMA
    '#0081c4',  // UControl - rgb(0, 129, 196)
    '#262438',  // CELA
    '#7bcd3b',  // AFT (default)
    '#1976d2',  // Material Blue
    '#d32f2f'   // Material Red
  ];

  constructor(
    private catalogService: CatalogService,
    private logger: LoggerService,
    private store: Store<AppStateInterface>,
    private dataService: DataService,
    private utilsService: UtilsService,
    private translateService: TranslateService
  ) {}

  stylesheetString: string = 'aft';

  ngOnInit(): void {
    this.stylesheetString = this.utilsService.getStylesheetString();
    this.logger.info('graphic-config', 'Component initialized');

    // Load current theme color from CSS custom property
    this.loadCurrentThemeColor();

    if (this.customerId) {
      this.currentCustomerUid = this.customerId;
      this.loadGraphicsConfiguration();
    }
    // Fallback per i contenitori che non passano customerId: la sottoscrizione si auto-ignora
    // quando l'input è valorizzato, così lo store non sovrascrive la scelta del contenitore.
    this.subscribeToCustomer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId'] && !changes['customerId'].firstChange) {
      const uid: string | null = changes['customerId'].currentValue;
      if (uid && uid !== this.currentCustomerUid) {
        this.logger.debug('graphic-config', 'Customer changed, reloading configuration');
        this.currentCustomerUid = uid;
        this.dataLoaded = false;
        this.loadGraphicsConfiguration();
      }
    }
    if (changes['selectedAudience'] && !changes['selectedAudience'].firstChange) {
      this.logger.debug('graphic-config', 'Audience changed, reloading configuration');
      this.dataLoaded = false;
      if (this.currentCustomerUid) {
        this.loadGraphicsConfiguration();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Metodo pubblico per forzare il ricaricamento dei dati
   */
  public reloadData(): void {
    this.logger.info('graphic-config', 'Ricaricamento dati richiesto');
    if (this.currentCustomerUid) {
      this.dataLoaded = false;
      this.loadGraphicsConfiguration();
    } else {
      this.logger.warn('graphic-config', 'Impossibile ricaricare: customer UID mancante');
    }
  }

  /**
   * Sottoscrizione al customer selezionato
   */
  private subscribeToCustomer(): void {
    this.store.select(selectedCustomerSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((customer: SelectedCustomerCacheInterface) => {
        // Se il contenitore passa un customer esplicito quella scelta vince: ignoriamo lo store.
        if (this.customerId) return;
        if (customer?.uid) {
          const customerChanged = this.currentCustomerUid !== customer.uid;
          this.currentCustomerUid = customer.uid;
          
          if (customerChanged) {
            this.dataLoaded = false;
          }
          
          if (!this.dataLoaded) {
            this.loadGraphicsConfiguration();
          }
        }
      });
  }

  /**
   * Carica la configurazione grafica salvata
   */
  private loadGraphicsConfiguration(): void {
    if (!this.currentCustomerUid) {
      this.logger.warn('graphic-config', 'Customer UID mancante');
      return;
    }

    const userInfo = this.dataService.getCurrentUserInfo();
    const uidUser = userInfo.user?.uid;

    if (!uidUser) {
      this.logger.warn('graphic-config', 'User UID mancante');
      return;
    }

    this.isLoading = true;

    this.catalogService.getGraphicsConfigFromAPI(this.currentCustomerUid, uidUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (configs) => {
          this.dataLoaded = true;
          
          if (configs && configs.length > 0) {
            // Prendi la configurazione più recente
            const latestConfig = configs[0];
            this.configId = latestConfig?.id ?? null;
            this.configCustomers = latestConfig?.config?.customers ?? null;
            this.configOwnerUid = latestConfig?.uidCustomer ?? latestConfig?.config?.customers?.[0] ?? null;
            const graphicsData = latestConfig?.config?.mappingData;

            if (graphicsData) {
              this.logger.debug('graphic-config', 'Configurazione graphics trovata', graphicsData);
              
              // Applica la configurazione caricata
              this.logo = graphicsData.logo || null;
              this.logoPreview = this.logo;
              this.primaryColor = graphicsData.primaryColor || '#1976d2';
              this.portalTitle = graphicsData.portalTitle || '';

              this.logger.info('graphic-config', 'Configurazione graphics applicata');
            } else {
              this.logger.debug('graphic-config', 'Nessuna configurazione graphics trovata nei dati');
              this.resetToDefaults();
            }
          } else {
            this.logger.debug('graphic-config', 'Nessuna configurazione graphics salvata');
            this.configId = null;
            this.configCustomers = null;
            this.configOwnerUid = null;
            this.resetToDefaults();
          }

          this.isLoading = false;
        },
        error: (error) => {
          this.logger.error('graphic-config', 'Errore caricamento configurazione graphics', error);
          this.resetToDefaults();
          this.isLoading = false;
          this.dataLoaded = true;
        }
      });
  }

  /**
   * Reset ai valori di default
   */
  private resetToDefaults(): void {
    this.logo = null;
    this.logoPreview = null;
    this.loadCurrentThemeColor(); // Reset to current theme color instead of Material Blue
    this.portalTitle = '';
  }

  /**
   * Load the current theme color from CSS custom property --main-color
   * This ensures the component starts with the customer's brand color
   */
  private loadCurrentThemeColor(): void {
    const mainColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--main-color')
      .trim();
    
    if (mainColor) {
      // Convert rgb() format to hex if needed
      this.defaultThemeColor = this.rgbToHex(mainColor);
      this.primaryColor = this.defaultThemeColor;
      this.logger.debug('graphic-config', 'Loaded current theme color:', this.primaryColor);
    } else {
      // Fallback to default
      this.defaultThemeColor = '#7bcd3b';
      this.primaryColor = this.defaultThemeColor;
      this.logger.warn('graphic-config', 'Could not load --main-color, using default');
    }
  }

  /**
   * Convert RGB color string to hex format
   * Handles both rgb(r, g, b) and hex formats
   */
  private rgbToHex(color: string): string {
    // If already hex, return as-is
    if (color.startsWith('#')) {
      return color;
    }

    // Parse rgb(r, g, b) format
    const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      return '#' + [r, g, b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      }).join('');
    }

    // If can't parse, return as-is
    return color;
  }

  /**
   * Gestisce il caricamento del logo
   */
  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      
      // Validazione tipo file
      if (!file.type.startsWith('image/')) {
        this.logger.warn('graphic-config', 'File selezionato non è un\'immagine');
        alert(this.translateService.instant('confSignalsPanel.invalidImageFileError'));
        return;
      }

      // Validazione dimensione (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        this.logger.warn('graphic-config', 'File troppo grande');
        alert(this.translateService.instant('confSignalsPanel.imageSizeExceededError'));
        return;
      }

      // Converte l'immagine in Base64
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const base64 = e.target?.result as string;
        this.logo = base64;
        this.logoPreview = base64;
        this.logger.debug('graphic-config', 'Logo caricato con successo');
      };
      reader.onerror = () => {
        this.logger.error('graphic-config', 'Errore nella lettura del file');
        alert(this.translateService.instant('confSignalsPanel.fileReadError'));
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Rimuove il logo
   */
  removeLogo(): void {
    this.logo = null;
    this.logoPreview = null;
    this.logger.debug('graphic-config', 'Logo rimosso');
  }

  /**
   * Gestisce il cambio del colore primario
   */
  onColorChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.primaryColor = input.value;
    this.logger.debug('graphic-config', 'Colore primario cambiato a:', this.primaryColor);
  }

  /**
   * Gestisce il cambio del titolo del portale
   */
  onTitleChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.portalTitle = input.value;
    this.logger.debug('graphic-config', 'Titolo portale cambiato a:', this.portalTitle);
  }

  /**
   * Reset the primary color to the default theme color
   */
  resetColor(): void {
    this.primaryColor = this.defaultThemeColor;
    this.logger.info('graphic-config', 'Primary color reset to default:', this.defaultThemeColor);
  }

  /**
   * Metodo pubblico per ottenere la configurazione corrente
   * Chiamato dal componente parent per salvare
   */
  public getGraphicsConfiguration(): GraphicsConfiguration {
    const config: GraphicsConfiguration = {
      logo: this.logo || undefined,
      primaryColor: this.primaryColor,
      portalTitle: this.portalTitle || undefined
    };

    this.logger.debug('graphic-config', 'Configurazione graphics da salvare:', config);
    return config;
  }

  /**
   * Verifica se ci sono modifiche rispetto ai valori di default
   */
  hasChanges(): boolean {
    return this.logo !== null || 
           this.primaryColor !== this.defaultThemeColor || 
           this.portalTitle !== '';
  }
}
